import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export const fmtDate = (v?: string | null) => (v ? dayjs(v).format('D MMM YYYY, HH:mm') : '—');
export const fmtRelative = (v?: string | null) => (v ? dayjs(v).fromNow() : '—');
export const fmtDay = (v: string) => dayjs(v).format('D MMM');

export const fmtNumber = (n?: number | null) =>
  n === null || n === undefined ? '—' : new Intl.NumberFormat().format(n);

/** Percentage of a total, rendered as '—' rather than NaN when the total is zero. */
export const fmtPercent = (part: number, total: number) =>
  total === 0 ? '—' : `${((part / total) * 100).toFixed(1)}%`;

/**
 * Colour for a delivery or message status.
 *
 * `skipped` is grey rather than red on purpose: a gate refused it (suppressed,
 * opted out, capped, dry run), which is the system working correctly. Colouring
 * it as a failure would train operators to ignore real failures.
 */
export function statusColor(status: string): string {
  switch (status) {
    case 'delivered':
    case 'completed':
      return 'teal';
    case 'sent':
      return 'blue';
    case 'queued':
    case 'sending':
    case 'pending':
      return 'gray';
    case 'retry':
      return 'yellow';
    case 'failed':
      return 'red';
    case 'skipped':
      return 'gray';
    case 'live':
      return 'teal';
    case 'paused':
      return 'yellow';
    case 'draft':
      return 'gray';
    case 'archived':
      return 'dark';
    default:
      return 'gray';
  }
}

/**
 * Colour for a normalized error code, grouped by what the operator must DO.
 *
 * Red is reserved for the codes that need a human — credentials and quota. Dead
 * tokens and bounces are orange because they are routine hygiene the system
 * already handled, and colouring them identically to a broken integration is how
 * a dashboard stops being useful.
 */
export function errorColor(code: string): string {
  switch (code) {
    case 'AUTH_FAILED':
    case 'QUOTA_EXCEEDED':
    case 'TEMPLATE_REJECTED':
      return 'red';
    case 'UNREGISTERED':
    case 'INVALID_ADDRESS':
    case 'HARD_BOUNCE':
    case 'COMPLAINT':
      return 'orange';
    case 'RATE_LIMITED':
    case 'PROVIDER_ERROR':
    case 'SOFT_BOUNCE':
    case 'TIMEOUT':
      return 'yellow';
    default:
      return 'gray';
  }
}

/** Plain-English explanation of a taxonomy code, shown next to the badge. */
export const ERROR_HELP: Record<string, string> = {
  UNREGISTERED: 'The token or subscription is gone. The device was marked dead automatically.',
  INVALID_ADDRESS: 'The address is malformed or does not exist.',
  HARD_BOUNCE: 'Permanent delivery failure. The address was added to suppressions.',
  SOFT_BOUNCE: 'Temporary failure, such as a full mailbox. Retried with backoff.',
  COMPLAINT: 'Marked as spam. Suppressed and the preference was switched off.',
  RATE_LIMITED: 'The provider throttled us. Retried honouring Retry-After.',
  PROVIDER_ERROR: 'The provider returned a server error. Retried with backoff.',
  AUTH_FAILED: 'The credentials were rejected. The channel was halted — fix the integration.',
  TEMPLATE_REJECTED: 'The template is not approved (DLT or WhatsApp), so the send failed fast.',
  QUOTA_EXCEEDED: 'The provider account hit a limit. The channel was halted.',
  SUPPRESSED: 'The address is on the suppression list. It never reached a provider.',
  OPTED_OUT: 'The recipient opted out of this category and channel.',
  QUIET_HOURS: 'Held by quiet hours; rescheduled for the end of the window.',
  FREQUENCY_CAPPED: 'Held by the frequency cap; rescheduled rather than dropped.',
  DRY_RUN: 'Dry run: every gate passed and the content rendered, but nothing was dispatched.',
  NO_ADDRESS: 'No usable address for this channel.',
  TIMEOUT: 'The provider did not respond in time. Retried.',
};

export const CHANNEL_LABEL: Record<string, string> = {
  push: 'Push',
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};
